import betslip from '@gin-betting/betslip';
import core from '@gin-betting/core';
import { createSelector } from 'reselect';

import { useMemoSelector } from 'hooks';

const {
    entities: {
        selectors: {
            makeMatchSelector,
            makeMarketSelector,
            selectOdd,
            selectMarketIdFromProps,
        },
    },
    utils: { getIdsFromBetslipOddId },
} = core;

const {
    selectors: { selectBetslipBet, selectBetslipOdd },
} = betslip;

const matchPropsToPick = [
    'id' as const,
    'status' as const,
    'title' as const,
    'startTime' as const,
    'disabled' as const,
    'betStop' as const,
];

const marketPropsToPick = ['name' as const, 'status' as const];

const selectMatch = makeMatchSelector({
    propsToPick: matchPropsToPick,
});
const selectMarket = makeMarketSelector({
    propsToPick: marketPropsToPick,
    selectMarketId: selectMarketIdFromProps,
});

interface Input {
    betslipOddId: string;
}

type Output = {
    betslipOdd: NonNullable<ReturnType<typeof selectBetslipOdd>>;
    betslipBet: NonNullable<ReturnType<typeof selectBetslipBet>>;
    odd: NonNullable<ReturnType<typeof selectOdd>>;
    market: NonNullable<ReturnType<typeof selectMarket>>;
    match: NonNullable<ReturnType<typeof selectMatch>>;
} | null;

export type ValidOddProps = NonNullable<ReturnType<typeof useValidOddProps>>;

const selectProps = createSelector(
    selectBetslipOdd,
    selectBetslipBet,
    selectOdd,
    selectMarket,
    selectMatch,
    (betslipOdd, betslipBet, odd, market, match) => {
        if (!betslipOdd || !match || !market || !odd || !betslipBet) {
            return null;
        }

        return {
            betslipOdd,
            betslipBet,
            odd,
            market,
            match,
        };
    }
);

const useValidOddProps = ({ betslipOddId }: Input): Output => {
    const { matchId, marketId, oddId, betslipBetId } = getIdsFromBetslipOddId(
        betslipOddId
    );

    return useMemoSelector(selectProps, {
        betslipOddId,
        betslipBetId,
        oddId,
        marketId,
        matchId,
    });
};

export default useValidOddProps;
